import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import type { SiteSeparationRuleType } from 'components/SiteSeparation/model/SiteSeparationRuleType';

const ruleTypes: { [Key in SiteSeparationRuleType]: string } = {
  regexHostname: 'Hostname (On-premise network)',
  regexSnmpLocation: 'SNMP location (On-premise network)',
  routingSwitching: 'Routing and switching domain (On-premise network)',
  regexCloudResourceId: 'Cloud Resource ID (Cloud network)',
  cloudResourceTag: 'Tag (Cloud network)',
  slug: 'Slug (Cloud network)',
};

const transformationOptions: {
  [Key in SiteSeparationAttributeTransformation]: string;
} = {
  lowercase: 'lower case',
  none: 'None',
  uppercase: 'UPPER CASE',
};

export const siteSeparationMessages = {
  en: {
    loadingError: 'Failed to load site separation',
    mainForm: {
      title: 'Site separation',
      description: (
        <p>
          Sites are separated collections of devices. Rule precedence is defined
          by the order from top to bottom.
        </p>
      ),
      btnSave: 'Save',
      firstRule: 'First rule',
      lastRule: 'Last rule',
      manualSiteSeparation: 'Manual site separation',
      manualSiteSeparationTooltip: (
        <span>
          The manual site separation respects the <code>siteName</code> device
          attribute that can be set in the Device Attributes table.
        </span>
      ),
      configureDeviceAttributes: 'Configure device attributes',
      neighborshipFallback:
        'Try to assign devices without sites based on device neighborship',
      expandRow: 'Show',
      collapseRow: 'Hide',
      emptyTable:
        'No site separation rule defined, define a new rule using "Add rule" button above.',
      emptyTableFiltered: 'No site separation rule matches current filter.',
      header: {
        priority: 'Priority',
        rule: 'Rule',
        note: 'Note',
      },
    },
    ruleTypes,
    addRule: {
      disabledRoutingSwitchingTooltip:
        'Only one rule of this type can be used.',
      placeholder: 'Select rule',
      submit: 'Add rule',
    },
    bulkActions: {
      moveBefore: 'Move before rule #',
      priorityChangeButton: 'Move',
      removeButton: 'Remove',
      selected:
        '{count, plural, one {# selected rule} other {# selected rules}}',
    },
    drag: {
      preview:
        '{count, plural, one {Change priority} other {Change priority (# selected)}}',
    },
    rowActions: {
      changePriority: 'Change priority',
      remove: 'Remove rule',
    },
    regexRuleForm: {
      labels: {
        regex: 'Regular Expression',
        regexTooltip:
          'You can use a capturing group (parentheses) to generate site name.',
        siteName: 'Site Name',
        siteNameGenerated: 'Generated automatically from first capturing group',
        hostnameTransformationTooltip:
          'Transform hostname before matching the regular expression and generating site name. For example, expression "(X.*)_.*" with transform "UPPER CASE" will match hostname "xa_11" into site "XA", while with "lower case" or "none" it won\'t match that hostname at all.',
        snmpTransformationTooltip:
          'Transform SNMP location before matching the regular expression and generating site name. For example, expression "(X.*)_.*" with transform "UPPER CASE" will match SNMP location "xa_11" into site "XA", while with "lower case" or "none" it won\'t match that SNMP location at all.',
        applyToCloudInstances:
          'Include also cloud devices discovered via SSH or Vendor API',
        cloudResourceIdTransformationTooltip:
          'Transform Cloud Resource ID before matching the regular expression and generating site name. For example, expression "(X.*)_.*" with transform "UPPER CASE" will match ID "xa_11" into site "XA", while with "lower case" or "none" it won\'t match that ID at all.',
        cloudResourceIdTransformationLabel: 'Transform Cloud Resource ID',
      },
    },
    routingSwitchingRuleForm: {
      labels: {
        wan: 'Excluded WAN interfaces',
        wanTooltip:
          'For networks that have direct routing connectivity between sites, such as DMVPN or Leased Lines (usually over Serial or MFR interfaces), an option to separate the site using tunnel and/or serial interface should be selected.',
        minimumSiteSize: 'Minimum site size',
        minimumSiteSizeSuffix: 'devices',
        firewall: 'Firewall at site',
        firewallTooltip: (
          <>
            For situations where inaccessible routed firewall is used at the
            site (i.e. device under different management team), an option{' '}
            <code>Firewall at site</code> can be turned on, so the
            infrastructure before and behind firewall is not separated into two
            different sites.
          </>
        ),
        transformationTooltip:
          'The Routing & Switching rule will split devices into groups (sites) where the site name is determined based on the "Longest common string" algorithm. For this algorithm it is recommended to set all characters in hostname to lower or upper case, the resulted site name will be affected too.',
      },
      errors: {
        minimumSiteSizeInvalidNumber: 'Invalid number.',
        minimumSiteSizeTooSmall: 'Minimum site size can not be less than 1.',
      },
      wanOptions: {
        serial: 'Serial',
        tunnel: 'Tunnel',
      },
    },
    removeRulesDialog: {
      header: 'Remove {count, plural, one {rule} other {# rules}}',
      subHeader:
        'Are you sure you want to remove {count, plural, one {the rule} other {these rules}}?',
      btnConfirm: 'Remove',
      btnCancel: 'Cancel',
    },
    ruleFormCommon: {
      labels: {
        hostnameTransformation: 'Transform hostname',
        snmpTransformation: 'Transform SNMP location',
        note: 'Note',
      },
      deleteRule: 'Delete rule',
      updateRule: 'Update rule',
      testRule: 'Test rule',
    },
    save: {
      saved: 'Site separation saved.',
      failedInvalid: 'Please fix invalid rules first',
    },
    search: {
      filterActive: 'Filter active',
      placeholder: 'Search',
    },
    transformationOptions,
    testRule: {
      hostnameModalTitle: 'Test Matched Hostnames',
      snmpModalTitle: 'Test Matched SNMP locations',
      cloudResourceIdModalTitle: 'Test Matched Cloud Resource IDs',
      testNow: 'Test Now',
      matched: 'Matched',
      unmatched: 'Unmatched',
      hostname: 'Hostname',
      site: 'Site',
      snmpLocation: 'SNMP location',
      apply: 'Apply expression',
      cancel: 'Cancel',
      emptyTable: {
        matched:
          'There are no devices in current snapshot that match the regex.',
        unmatched:
          'There are no devices in current snapshot that do not match the regex.',
      },
    },
    updateFromGlobalSettings: {
      button: 'Override with global settings',
      confirmDialog: {
        header: 'Current settings will be overridden',
        subHeader: 'Overwrite current settings?',
        btnConfirm: 'Overwrite',
        btnSecondaryConfirm: 'Cancel',
      },
    },
    globalPage: {
      leaveDialog: {
        header: 'Changes in site separation are not saved',
        subHeader: 'Do you really want to leave withou save?',
        save: 'Save',
        discard: 'Discard',
      },
    },
  },
};
